import type {
  ActivePurchaseResponse,
  CancelBookingResponse,
  ClientMeResponse,
  ClientTokenPairResponse,
  CreateBookingRequest,
  CreateBookingResponse,
  CreateLeadRequest,
  CreateLeadResponse,
  FingerImagePosition,
  GetClientQuestionnaireResponse,
  GetClientReportResponse,
  GetNarrationResponse,
  GetOpenSlotsResponse,
  HouseholdResponse,
  Language,
  ListBankAccountsResponse,
  ListBookingsResponse,
  ListClientQuestionnairesResponse,
  ListClientReportsResponse,
  ListClientResourcesResponse,
  ListFingerprintImagesResponse,
  ListPackagesResponse,
  ListPurchasesResponse,
  NarratableReportSection,
  PurchaseResponse,
  RegenerateReportLanguageResponse,
  RequestOtpResponse,
  RequestPurchaseRequest,
  RescheduleBookingRequest,
  RescheduleBookingResponse,
  SaveClientQuestionnaireRequest,
  SaveClientQuestionnaireResponse,
  SubmitClientQuestionnaireRequest,
  SubmitClientQuestionnaireResponse,
  SubmitOnboardingRequest,
  SubmitOnboardingResponse,
  SubmitPaymentSlipResponse,
  UploadFingerprintImageResponse,
} from '@brainkey/shared';

import { API_BASE_URL, API_PREFIX } from '../config';

import { apiRequest, ApiError } from './client';
import { tokenStore } from './tokenStore';

/** Public marketing site: package catalog + bank accounts + lead capture
 * (P21-T02/T03). No client-portal session required — `skipAuth: true`
 * even though a visitor who happens to already be logged in would have a
 * token silently attached otherwise (harmless, but these routes don't
 * need or check it). */
export const publicApi = {
  listPackages(signal?: AbortSignal): Promise<ListPackagesResponse> {
    return apiRequest<ListPackagesResponse>('/packages', {
      skipAuth: true,
      ...(signal ? { signal } : {}),
    });
  },

  listBankAccounts(signal?: AbortSignal): Promise<ListBankAccountsResponse> {
    return apiRequest<ListBankAccountsResponse>('/bank-accounts', {
      skipAuth: true,
      ...(signal ? { signal } : {}),
    });
  },

  submitLead(body: CreateLeadRequest): Promise<CreateLeadResponse> {
    return apiRequest<CreateLeadResponse>('/leads', { method: 'POST', body, skipAuth: true });
  },
};

/**
 * Client Portal auth (CP-P1-T01/DEC-0028). Every call here targets
 * `/api/v1/client/auth/*` — a fully separate token/audience stack from the
 * coach portal's `/api/v1/auth/*`.
 */
/** The logged-in guardian's switchable relatives. Not acting-as aware — the
 * household always belongs to the person who actually signed in. */
export const clientHouseholdApi = {
  list(signal?: AbortSignal): Promise<HouseholdResponse> {
    return apiRequest<HouseholdResponse>('/client/household', signal ? { signal } : {});
  },
};

export const clientAuthApi = {
  /** Step 1 — always resolves; the backend never reveals whether
   * `identifier` matched an invited client (enumeration resistance). */
  requestOtp(identifier: string): Promise<RequestOtpResponse> {
    return apiRequest<RequestOtpResponse>('/client/auth/request-otp', {
      method: 'POST',
      body: { identifier },
      skipAuth: true,
    });
  },

  /** Step 2 — the session-establishing call. */
  verifyOtp(identifier: string, code: string): Promise<ClientTokenPairResponse> {
    return apiRequest<ClientTokenPairResponse>('/client/auth/verify-otp', {
      method: 'POST',
      body: { identifier, code },
      skipAuth: true,
    });
  },

  logout(refreshToken: string): Promise<void> {
    return apiRequest<void>('/client/auth/logout', {
      method: 'POST',
      body: { refreshToken },
      skipAuth: true,
    });
  },

  /** "Sign out of all devices" (CP-P4-T03/DEC-0039) — revokes every refresh
   * token for the account. Requires a valid access token (unlike `logout`,
   * which takes a refresh token), so this call is NOT `skipAuth`. */
  logoutAll(): Promise<void> {
    return apiRequest<void>('/client/auth/logout-all', { method: 'POST' });
  },

  me(signal?: AbortSignal): Promise<ClientMeResponse> {
    return apiRequest<ClientMeResponse>('/client/auth/me', signal ? { signal } : {});
  },
};

/** Client self-service data rights (CP-P4-T03/DEC-0039). */
export const clientAccountApi = {
  /**
   * "Download my data" — deliberately NOT `apiRequest` (which always parses
   * JSON as a typed DTO): this is a one-off file download, not something
   * displayed in the UI, so it's fetched as a blob and saved client-side via
   * a synthetic anchor (mirrors `clientBookingsApi.downloadIcs`).
   */
  async downloadExport(): Promise<Blob> {
    const url = `${API_BASE_URL}${API_PREFIX}/client/me/export`;
    const token = tokenStore.getAccessToken();
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new ApiError(res.status, 'EXPORT_DOWNLOAD_FAILED', 'Could not download your data.');
    }
    return res.blob();
  },

  deleteRequest(): Promise<void> {
    return apiRequest<void>('/client/me/delete-request', { method: 'POST' });
  },
};

/** Client onboarding: intake (goals/background/language) + PDPA consent
 * (CP-P1-T03/DEC-0030). */
export const clientOnboardingApi = {
  submit(body: SubmitOnboardingRequest): Promise<SubmitOnboardingResponse> {
    return apiRequest<SubmitOnboardingResponse>('/client/onboarding', {
      method: 'POST',
      body,
    });
  },
};

/** Client-facing questionnaire completion (CP-P2-T01/DEC-0031). */
export const clientQuestionnairesApi = {
  list(signal?: AbortSignal): Promise<ListClientQuestionnairesResponse> {
    return apiRequest<ListClientQuestionnairesResponse>(
      '/client/questionnaires',
      signal ? { signal } : {},
    );
  },

  get(id: string, signal?: AbortSignal): Promise<GetClientQuestionnaireResponse> {
    return apiRequest<GetClientQuestionnaireResponse>(
      `/client/questionnaires/${id}`,
      signal ? { signal } : {},
    );
  },

  save(id: string, body: SaveClientQuestionnaireRequest): Promise<SaveClientQuestionnaireResponse> {
    return apiRequest<SaveClientQuestionnaireResponse>(`/client/questionnaires/${id}/save`, {
      method: 'PATCH',
      body,
    });
  },

  submit(
    id: string,
    body: SubmitClientQuestionnaireRequest,
  ): Promise<SubmitClientQuestionnaireResponse> {
    return apiRequest<SubmitClientQuestionnaireResponse>(`/client/questionnaires/${id}/submit`, {
      method: 'POST',
      body,
    });
  },
};

/** Client-facing report status + viewing (CP-P2-T03/DEC-0033). Only ever
 * returns the client's own FULL-variant report(s). */
export const clientReportsApi = {
  list(signal?: AbortSignal): Promise<ListClientReportsResponse> {
    return apiRequest<ListClientReportsResponse>('/client/reports', signal ? { signal } : {});
  },

  getById(id: string, signal?: AbortSignal): Promise<GetClientReportResponse> {
    return apiRequest<GetClientReportResponse>(`/client/reports/${id}`, signal ? { signal } : {});
  },

  /** First call synthesizes + stores narration audio for this section
   * (CP-P3-T02); subsequent calls are a free re-signed read. */
  getNarration(id: string, section: NarratableReportSection): Promise<GetNarrationResponse> {
    return apiRequest<GetNarrationResponse>(`/client/reports/${id}/narration/${section}`, {
      method: 'POST',
    });
  },

  /** Explicit, client-triggered request for this report in a different
   * language — a real new generation unless one in that language already
   * exists (GENERATE ONCE). Never called as a side effect of the topbar
   * language toggle; only from the Report page's mismatch banner. */
  regenerateLanguage(id: string, language: Language): Promise<RegenerateReportLanguageResponse> {
    return apiRequest<RegenerateReportLanguageResponse>(
      `/client/reports/${id}/regenerate-language`,
      { method: 'POST', body: { language } },
    );
  },
};

/** Client Session Booking (CP-P4-T01/DEC-0037). */
export const clientBookingsApi = {
  getOpenSlots(from: string, to: string, signal?: AbortSignal): Promise<GetOpenSlotsResponse> {
    return apiRequest<GetOpenSlotsResponse>('/client/bookings/slots', {
      query: { from, to },
      ...(signal ? { signal } : {}),
    });
  },

  list(signal?: AbortSignal): Promise<ListBookingsResponse> {
    return apiRequest<ListBookingsResponse>('/client/bookings', signal ? { signal } : {});
  },

  create(body: CreateBookingRequest): Promise<CreateBookingResponse> {
    return apiRequest<CreateBookingResponse>('/client/bookings', { method: 'POST', body });
  },

  reschedule(id: string, body: RescheduleBookingRequest): Promise<RescheduleBookingResponse> {
    return apiRequest<RescheduleBookingResponse>(`/client/bookings/${id}/reschedule`, {
      method: 'PATCH',
      body,
    });
  },

  cancel(id: string): Promise<CancelBookingResponse> {
    return apiRequest<CancelBookingResponse>(`/client/bookings/${id}/cancel`, {
      method: 'PATCH',
    });
  },

  /**
   * `.ics` download — deliberately NOT `apiRequest` (which always parses
   * JSON): this is behind `authenticateClient` (needs the Bearer token,
   * unlike the PDF viewer's signed-URL `<a href>`) but returns
   * `text/calendar`, so it's fetched as a blob and saved client-side via a
   * synthetic anchor (see DEC-0037 for why this differs from the PDF flow).
   */
  async downloadIcs(id: string): Promise<Blob> {
    const url = `${API_BASE_URL}${API_PREFIX}/client/bookings/${id}/ics`;
    const token = tokenStore.getAccessToken();
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'ICS_DOWNLOAD_FAILED',
        'Could not download the calendar file.',
      );
    }
    return res.blob();
  },
};

/** Package purchase requests + payment-slip upload (P21-T04). Every call
 * here targets `/api/v1/client/purchases/*` — client-token-gated, the
 * subject is always the authenticated client, never a route param. */
export const clientPurchasesApi = {
  request(body: RequestPurchaseRequest): Promise<PurchaseResponse['purchase']> {
    return apiRequest<PurchaseResponse>('/client/purchases', { method: 'POST', body }).then(
      (res) => res.purchase,
    );
  },

  async listMine(signal?: AbortSignal): Promise<ListPurchasesResponse['purchases']> {
    const res = await apiRequest<ListPurchasesResponse>(
      '/client/purchases',
      signal ? { signal } : {},
    );
    return res.purchases;
  },

  async getActive(signal?: AbortSignal): Promise<ActivePurchaseResponse['purchase']> {
    const res = await apiRequest<ActivePurchaseResponse>(
      '/client/purchases/active',
      signal ? { signal } : {},
    );
    return res.purchase;
  },

  async getById(id: string, signal?: AbortSignal): Promise<PurchaseResponse['purchase']> {
    const res = await apiRequest<PurchaseResponse>(
      `/client/purchases/${id}`,
      signal ? { signal } : {},
    );
    return res.purchase;
  },

  async submitSlip(purchaseId: string, file: File): Promise<SubmitPaymentSlipResponse['slip']> {
    const form = new FormData();
    form.append('file', file);
    const res = await apiRequest<SubmitPaymentSlipResponse>(
      `/client/purchases/${purchaseId}/slip`,
      { method: 'POST', body: form },
    );
    return res.slip;
  },
};

/** The client's own 10 fingerprint images, one per finger — a photo the
 * client uploads themselves (a phone photo of an inked/scanned print).
 * Every call targets `/api/v1/client/fingerprint-images/*` — client-token-
 * gated, the subject is always the authenticated client, never a route
 * param. Uploading again for a finger replaces the previous image. */
export const clientFingerprintImagesApi = {
  async listMine(signal?: AbortSignal): Promise<ListFingerprintImagesResponse['images']> {
    const res = await apiRequest<ListFingerprintImagesResponse>(
      '/client/fingerprint-images',
      signal ? { signal } : {},
    );
    return res.images;
  },

  async upload(
    finger: FingerImagePosition,
    file: File,
  ): Promise<UploadFingerprintImageResponse['image']> {
    const form = new FormData();
    form.append('file', file);
    const res = await apiRequest<UploadFingerprintImageResponse>(
      `/client/fingerprint-images/${finger}`,
      { method: 'POST', body: form },
    );
    return res.image;
  },

  remove(finger: FingerImagePosition): Promise<void> {
    return apiRequest<void>(`/client/fingerprint-images/${finger}`, { method: 'DELETE' });
  },

  /** Deliberately NOT `apiRequest` (which always parses JSON) — the
   * dual-audience `/api/v1/fingerprint-images/*` route streams the file
   * directly, no public signed URL, so it's fetched as a blob with the
   * bearer token attached and rendered via an object URL. */
  async getMyImageBlob(finger: FingerImagePosition, clientId: string): Promise<Blob> {
    const url = `${API_BASE_URL}${API_PREFIX}/fingerprint-images/${clientId}/${finger}`;
    const token = tokenStore.getAccessToken();
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'FINGERPRINT_IMAGE_DOWNLOAD_FAILED',
        'Could not load this fingerprint image.',
      );
    }
    return res.blob();
  },
};

/** Resources (P23) ever sent to this client by their coach — a coach-
 * supplied PDF library, never AI-generated. `downloadUrl` is a freshly
 * signed URL (like a report PDF), so no separate authenticated streaming
 * route is needed here (unlike fingerprint images above). */
export const clientResourcesApi = {
  async listMine(signal?: AbortSignal): Promise<ListClientResourcesResponse['resources']> {
    const res = await apiRequest<ListClientResourcesResponse>(
      '/client/resources',
      signal ? { signal } : {},
    );
    return res.resources;
  },
};

/** Web push subscribe/unsubscribe (CP-P4-T02/DEC-0038). Body shapes mirror
 * the browser's native `PushSubscriptionJSON` — no shared DTO needed since
 * nothing else in the platform consumes this shape. */
export interface SubscribePushRequest {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export const clientPushApi = {
  subscribe(body: SubscribePushRequest): Promise<void> {
    return apiRequest<void>('/client/push/subscribe', { method: 'POST', body });
  },

  unsubscribe(endpoint: string): Promise<void> {
    return apiRequest<void>('/client/push/unsubscribe', { method: 'POST', body: { endpoint } });
  },
};
